import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { ProtectedRoute } from '@shared/auth/ProtectedRoute';
import { RoleGuard } from '@shared/auth/RoleGuard';
import { LoginScreen } from './screens/Login/LoginScreen';
import { CallbackScreen } from './screens/Callback/CallbackScreen';
import { ConsoleShell } from './shell/ConsoleShell';
import { InventoryPane } from './screens/Inventory/InventoryPane';
import { DiagnosePane } from './screens/Diagnose/DiagnosePane';
import { ActPane } from './screens/Act/ActPane';
import { KnowledgePane } from './screens/Knowledge/KnowledgePane';
import { AuditPane } from './screens/Audit/AuditPane';
import { TicketsPane } from './screens/Tickets/TicketsPane';

// Lazy: this is the only pane that pulls in recharts (~400KB), and
// every other pane is on the critical path for an engineer opening the
// console during an incident. Splitting it keeps the initial bundle
// where it was rather than doubling it for a tab nobody opens first.
const MetricsPane = lazy(() =>
  import('./screens/Metrics/MetricsPane').then((m) => ({ default: m.MetricsPane })),
);

// Plan, 3b: L2/L3/platform_admin only -- READ is cross-site-broad for
// these three roles specifically (see RoleToken's own docstring); no
// other role in the shared vocabulary (site_supervisor, foh_operator,
// service_operator) has any business in this app at all.
const CONSOLE_ROLES = ['nexsoss:platform_admin', 'nexsoss:support_l2', 'nexsoss:support_l3'] as const;

function AccessDenied() {
  return (
    <div style={{ padding: 48, textAlign: 'center' }}>
      <h1>Access denied</h1>
      <p style={{ color: 'var(--nx-muted)' }}>
        This console requires a platform support role (L2, L3, or platform admin).
      </p>
    </div>
  );
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginScreen />} />
      <Route path="/callback" element={<CallbackScreen />} />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <RoleGuard allowedRoles={[...CONSOLE_ROLES]} fallback={<AccessDenied />}>
              <ConsoleShell />
            </RoleGuard>
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="inventory" replace />} />
        <Route path="inventory" element={<InventoryPane />} />
        <Route path="diagnose" element={<DiagnosePane />} />
        <Route path="act" element={<ActPane />} />
        <Route path="knowledge" element={<KnowledgePane />} />
        <Route path="tickets" element={<TicketsPane />} />
        <Route path="audit" element={<AuditPane />} />
        <Route
          path="metrics"
          element={
            <Suspense fallback={<div style={{ color: 'var(--nx-muted)' }}>Loading charts…</div>}>
              <MetricsPane />
            </Suspense>
          }
        />
      </Route>
    </Routes>
  );
}
